import React, { Fragment, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import LoadPage from "../common/LoadPage";
import AlertMessage from "../common/AlertMessage";
import { replaceUnderscoresAndUppercase, getRiskSeverity, getScoreBorderClass } from "../common/helpers";

import useAxiosFetch from "../common/useAxiosFetch";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import moment from "moment";
import { categoryIDtoLabel, getCategoryValue } from "../common/helpers";
import { MODAL_TYPES } from "../modals/ModalManager";

const RiskReviewPage = ({ refreshData, setRefreshData }) => {
  const { currentOrg, currentProject, currentUser, showAlertMessage, alertMessage, clearAlertMessage, openModal } = useAppContext();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Add custom styles
  useEffect(() => {
    const styleId = 'risk-review-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .pending-review-row {
          background-color: #fffbf0 !important;
          border-left: 4px solid #f39c12;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  const [sortColumn, setSortColumn] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [riskOwnerFilter, setRiskOwnerFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('pending'); // Default to pending reviews
  const [riskScoreFilter, setRiskScoreFilter] = useState('');
  const [visibleItems, setVisibleItems] = useState({});
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const headerRef = useRef();

  const navigate = useNavigate();
  const { data, loading, refetch } = useAxiosFetch("/api/risks/" + (currentProject.id ? "byProject/" + currentProject.id : "byOrg/" + currentOrg.id));

  //forces a refresh when risk is reviewed/updated
  useEffect(() => {
    if (refreshData > 0)
      refetch();
  }, [refreshData]);

  // Set filters from state or URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setRiskOwnerFilter(params.get('riskOwnerFilter') || '');
    setApprovalFilter(params.get('approvalFilter') || 'pending');
    setRiskScoreFilter(params.get('riskScoreFilter') || '');
  }, [location.search]);

  // add border bottom to table head when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        setIsHeaderStuck(headerRef.current.getBoundingClientRect().bottom <= 140);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading) return <LoadPage />;

  const uniqueRiskOwners = [...new Set(data.map(item => item.owner))];

  // Calculate counts from all data (not filtered by approval status)
  const pendingCount = data.filter(item => item.riskApproved === undefined).length;
  const approvedCount = data.filter(item => item.riskApproved === true).length;
  const rejectedCount = data.filter(item => item.riskApproved === false).length;

  const filteredData = data.filter(item => {
    if (riskOwnerFilter && item.owner !== riskOwnerFilter) return false;

    if (approvalFilter) {
      if (approvalFilter === 'approved' && item.riskApproved !== true) return false;
      if (approvalFilter === 'rejected' && item.riskApproved !== false) return false;
      if (approvalFilter === 'pending' && item.riskApproved !== undefined) return false;
    }

    switch (riskScoreFilter) {
      case 'low':
        if (getRiskSeverity(item["actual-chance"] * item['actual-impact'], currentOrg.rangeBounds) !== 'low') return false;
        break;
      case 'medium':
        if (getRiskSeverity(item["actual-chance"] * item['actual-impact'], currentOrg.rangeBounds) !== 'medium') return false;
        break;
      case 'high':
        if (getRiskSeverity(item["actual-chance"] * item['actual-impact'], currentOrg.rangeBounds) !== 'high') return false;
        break;
    }

    return true;
  });

  // Sorting function
  const sortedData = [...filteredData].sort((a, b) => {
    let comparison = 0;
    switch (sortColumn) {
      case 'description': comparison = a.description.localeCompare(b.description); break;
      case 'measures': comparison = a.measures.length - b.measures.length; break;
      case 'actual-score': comparison = a['actual-chance'] * a['actual-impact'] - b['actual-chance'] * b['actual-impact']; break;
      case 'expected-score': comparison = a['expected-chance'] * a['expected-impact'] - b['expected-chance'] * b['expected-impact']; break;  
      case 'owner': comparison = a.owner.localeCompare(b.owner); break;
      case 'updatedAt': comparison = (a.updatedAt || a.createdAt || a._ts) - (b.updatedAt || b.createdAt || b._ts); break;
      case 'riskApproved': {
        const getApprovalOrder = (val) => {
          if (val === true) return 1;
          if (val === undefined) return 2;
          if (val === false) return 3;
          return 4;
        };
        comparison = getApprovalOrder(a.riskApproved) - getApprovalOrder(b.riskApproved);
        break;
      }
      default: break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleSort = column => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleVisibility = (id) => {
    setVisibleItems(prevState => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  // Update URL with filters
  const updateURLWithState = () => {
    const params = new URLSearchParams();
    params.set('riskOwnerFilter', riskOwnerFilter);
    params.set('approvalFilter', approvalFilter);
    params.set('riskScoreFilter', riskScoreFilter);
    navigate(`?${params.toString()}`);
  };

  return (
    <div id="content">

      {/* Stats Overview */}
      <div className="content-bubble" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700 }}>
            {t('risk_review')}
          </div>
          <div 
            className="score-border-yellow score-chip" 
            style={{ 
              padding: '4px 10px', 
              cursor: 'pointer',
              background: approvalFilter === 'pending' ? 'var(--yellow-risk-score)' : 'transparent',
              color: approvalFilter === 'pending' ? 'white' : 'inherit'
            }} 
            onClick={() => setApprovalFilter(approvalFilter === 'pending' ? '' : 'pending')}
          >
            {t('pending')}: {pendingCount}
          </div>
          <div 
            className="score-border-green score-chip" 
            style={{ 
              padding: '4px 10px', 
              cursor: 'pointer',
              background: approvalFilter === 'approved' ? 'var(--green-risk-score)' : 'transparent',
              color: approvalFilter === 'approved' ? 'white' : 'inherit'
            }} 
            onClick={() => setApprovalFilter(approvalFilter === 'approved' ? '' : 'approved')}
          >
            {t('approved')}: {approvedCount}
          </div>
          <div 
            className="score-border-red score-chip" 
            style={{ 
              padding: '4px 10px', 
              cursor: 'pointer',
              background: approvalFilter === 'rejected' ? 'var(--red-risk-score)' : 'transparent',
              color: approvalFilter === 'rejected' ? 'white' : 'inherit'
            }} 
            onClick={() => setApprovalFilter(approvalFilter === 'rejected' ? '' : 'rejected')}
          >
            {t('rejected')}: {rejectedCount}
          </div>
          <div style={{ marginLeft: 'auto', opacity: 0.8 }}>
            {t('total_risks')}: <strong>{filteredData.length}</strong>&nbsp;
            {filteredData.length !== data.length && (
              <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                ({t('filtered_from')} {data.length})
              </span>
            )}
          </div>
        </div>
        

      </div>

      {/* Filters */}
      <div className="content-bubble" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ fontWeight: 600 }}>{t('filters')}:</div>
          
          <select
            className="blue-dropdown"
            value={approvalFilter}
            style={{ minWidth: 160 }}
            onChange={(e) => setApprovalFilter(e.target.value)}
          >
            <option value="">{t('all_statuses')}</option>
            <option value="pending">{t('pending_review')}</option>
            <option value="approved">{t('approved')}</option>
            <option value="rejected">{t('rejected')}</option>
          </select>

          <select
            className="blue-dropdown"
            value={riskOwnerFilter}
            style={{ minWidth: 180 }}
            onChange={e => setRiskOwnerFilter(e.target.value)}
          >
            <option value="">{t('all_owners')}</option>
            {uniqueRiskOwners.map(owner => (
              <option key={owner} value={owner}>
                {owner}
              </option>
            ))}
          </select>

          <select 
            className="blue-dropdown" 
            value={riskScoreFilter} 
            style={{ minWidth: 140 }}
            onChange={(e) => setRiskScoreFilter(e.target.value)}
          >
            <option value="">{t('all_scores')}</option>
            <option value="low">{t('low')}</option>
            <option value="medium">{t('medium')}</option>
            <option value="high">{t('high')}</option>
          </select>

          <select 
            className="blue-dropdown sort-risks-header" 
            value={sortColumn} 
            style={{ minWidth: 160 }}
            onChange={(e) => {
              const selectedColumn = e.target.value;
              setSortDirection(selectedColumn === 'updatedAt' || selectedColumn === 'actual-score' ? 'desc' : 'asc');
              setSortColumn(selectedColumn);
            }}
          >
            <option value="">↑↓ {t('sort_by')}</option>
            {['updatedAt', 'description', 'measures', 'actual-score', 'expected-score', 'owner', 'riskApproved'].map(column => (
              <option key={column} value={column}>
                {t(column)} {sortColumn === column && (sortDirection === 'asc' ? '↑' : '↓')}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="clear-both" />

      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      <div className="content-bubble content-bubble-table">
        <div className="table-wrapper">
          <table className="overview-table risk-review-table">
            <thead className={`sticky-header ${isHeaderStuck ? "stuck-header" : ""}`} ref={headerRef}>
              <tr>
                <td className="align-left pointer-class" onClick={() => handleSort('description')}>
                  {t('risk_description')} {sortColumn === 'description' && (sortDirection === 'asc' ? '↑' : '↓')}
                </td>
                <td colSpan="2">Score</td>
                <td className="pointer-class" onClick={() => handleSort('owner')}>
                  {t('risk_owner')} {sortColumn === 'owner' && (sortDirection === 'asc' ? '↑' : '↓')}
                </td>
                <td className="pointer-class" onClick={() => handleSort('riskApproved')}>
                  {t('approval_status')} {sortColumn === 'riskApproved' && (sortDirection === 'asc' ? '↑' : '↓')}
                </td>
                <td >{t('actions')}</td>
              </tr>
              <tr>
                <td>&nbsp;</td>
                <td onClick={() => handleSort('actual-score')} className="pointer-class initial-table-header">
                  <div>{t('current')}{sortColumn === 'actual-score' && (sortDirection === 'asc' ? '↑' : '↓')}</div>
                </td>
                <td onClick={() => handleSort('expected-score')} className="pointer-class expected-table-header">
                  <div>{t('target')}{sortColumn === 'expected-score' && (sortDirection === 'asc' ? '↑' : '↓')}</div>
                </td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px 20px', color: '#6c757d' }}>
                    <div>
                      <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                        {approvalFilter === 'pending' ? t('no_pending_reviews') : t('no_risks_match_filters')}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : sortedData.map((item, index) => {
                const isHighlighted = item.riskApproved === undefined;
                return (
                  <Fragment key={item["id"]}>
                    <tr className={isHighlighted ? "pending-review-row" : ""}>
                      <td className="pointer-class align-left">
                        <Link to={`/viewrisk?riskId=${item["id"]}`} className="risk-table-link" onClick={() => updateURLWithState()}>
                        <div className="risk-description">
                          {item.description}
                          <div className="risk-meta">
                            <span className="treatment-info">
                              {t('treatment')}: {replaceUnderscoresAndUppercase(item.treatment ? t(item.treatment) : t('not_set'))}
                            </span>
                            {item.measures?.length > 0 && (
                              <span 
                                className="measures-info pointer-class"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleVisibility(item["id"]);
                                }}
                              >
                                {item.measures.length} {t('measures')} {visibleItems[item["id"]] ? '▲' : '▼'}
                              </span>
                            )}
                          </div>
                        </div>
                        </Link>
                      </td>

                      <td className="current-table-column">
                        <div className={getScoreBorderClass(item['actual-chance'] * item['actual-impact'], currentOrg.rangeBounds)}
                        >
                          {item['actual-chance'] * item['actual-impact']}
                        </div>
                      </td>
                      <td className="expected-table-column">{item['expected-chance'] * item['expected-impact']}</td>
                      <td>{item.owner}</td>
                      <td>
                        {item.riskApproved === true && (
                          <span style={{ color: 'var(--green-risk-score)', fontWeight: 'bold' }}>
                            <i className="icon-check-circle" /> {t('approved')}
                          </span>
                        )}
                        {item.riskApproved === false && (
                          <span style={{ color: 'var(--red-risk-score)', fontWeight: 'bold' }}>
                            <i className="icon-cross-circle" /> {t('rejected')}
                          </span>
                        )}
                        {item.riskApproved === undefined && (
                          <span style={{ color: 'var(--yellow-risk-score)', fontWeight: 'bold' }}>
                            <i className="icon-clock" /> {t('pending')}
                          </span>
                        )}
                      </td>
                      <td>
                        <div 
                          className="white-border-btn" 
                          onClick={() => openModal(MODAL_TYPES.REVIEW_RISK, { riskObj: data.find((risk) => risk['id'] === item["id"]) })}
                          style={{ cursor: 'pointer', margin: '0 auto', float: 'none', textAlign: 'center', width: '60px' }}
                        >
                          <i className="icon-risk-review" />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan="7"
                        style={{ display: visibleItems[item["id"]] ? '' : 'none' }}
                        className="hidden-row"
                        id={'measures-' + item["id"]}
                      >
                        <table className="table-details">
                          <thead>
                            <tr>
                              <td>{t('measure')}</td>
                              <td>{t('category')}</td>
                              <td>{t('status')}</td>
                              <td>{t('owner_without_prefix')}</td>
                              <td>Deadline {t('date')}</td>
                              <td>{t('link_to_actions')}</td>
                            </tr>
                          </thead>
                          <tbody>
                            {item.measures?.map((measure, mIndex) => {
                              return (
                                <tr key={mIndex}>
                                  <td>{measure.measure}</td>
                                  <td>
                                    {measure.category && measure.category.map((cat, cIndex) => (
                                      <div key={cIndex}>{categoryIDtoLabel(getCategoryValue(cat), i18n.language, currentOrg.securityStandard)}</div>
                                    ))}
                                  </td>
                                  <td>{replaceUnderscoresAndUppercase(measure.status ? t(measure.status) : '')}</td>
                                  <td>{measure.owner}</td>
                                  <td>{measure.deadlineDate ? moment(measure.deadlineDate).format('DD-MM-YYYY') : ''}</td>
                                  <td>
                                    {measure.action && measure.action.startsWith('http') ? (
                                      <a href={measure.action} className="action-link" target="_blank" rel="noopener noreferrer">{t('go_to_action')}</a>
                                    ) : (
                                      measure.action
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr />
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RiskReviewPage;
